<template>
  <div
    v-if="isOpen"
    class="fixed inset-0 z-50 flex items-start justify-center pt-10 bg-black bg-opacity-50"
    @click="handleClickOutside"
  >
    <div
      class="relative w-3/4 h-auto p-6 overflow-y-auto bg-white rounded-lg"
      @click.stop
    >
      <h4 class="mb-4 text-xl font-bold">{{ title }}</h4>

      <div
        v-if="menuroles.find((menurole) => menurole.menu_id == props.menuId)"
      >
        <!-- Display Role Info and Program Study -->
        <div class="flow-root">
          <dl class="-my-3 text-sm divide-y divide-gray-100">
            <div class="grid grid-cols-1 gap-1 py-3 sm:grid-cols-3 sm:gap-4">
              <dt class="font-medium text-gray-900">Role Akses</dt>
              <dd class="text-gray-700 sm:col-span-2">
                <div
                  v-for="role in menuroles.find(
                    (menurole) => menurole.menu_id == props.menuId
                  ).roles"
                  :key="role.id"
                >
                  {{ role.title }}
                </div>
              </dd>
            </div>
            <div class="grid grid-cols-1 gap-1 py-3 sm:grid-cols-3 sm:gap-4">
              <dt class="font-medium text-gray-900">Program Studi</dt>
              <dd class="text-gray-700 sm:col-span-2">
                <div
                  v-for="program in menuroles.find(
                    (menurole) => menurole.menu_id == props.menuId
                  ).studyprograms"
                  :key="program.id"
                >
                  {{ program.jenjang }} - {{ program.name }}
                </div>
              </dd>
            </div>
          </dl>
        </div>
      </div>

      <!-- Input Fields for Role and Program -->
      <InputLabel for="role" value="Role Akses" class="mb-2" />

      <v-select
        v-model="form.role_id"
        multiple
        :reduce="(role) => role.id"
        :options="roles"
        label="title"
        class="block w-full py-2 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
      ></v-select>
      <InputError :message="form.errors.role_id" class="mt-2" />
      <InputLabel for="role" value="Users" class="mt-2" />

      <v-select
        v-model="form.user_id"
        multiple
        :reduce="(user) => user.id"
        :options="users"
        label="name"
        class="block w-full py-2 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
      ></v-select>
      <InputError :message="form.errors.user_id" class="mt-2" />
      <InputLabel for="role" value="Program Studi" class="mt-2" />
      <v-select
        v-model="prodiId"
        multiple
        :reduce="(program) => program.id"
        :options="programs"
        label="name"
        class="block w-full py-2 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
      ></v-select>
      <InputError :message="form.errors.idprodi" class="mt-2" />

      <!-- Action Buttons -->
      <div class="flex justify-end space-x-4">
        <button
          @click="submit"
          class="w-full px-4 py-2 font-semibold text-white shadow bold bg-blue hover:bg-blue-700 rounded-xl hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
        >
          Simpan
        </button>
        <button
          @click="closeModal"
          class="px-4 py-2 mt-4 text-white bg-red-500 rounded"
        >
          Tutup
        </button>
      </div>
    </div>
  </div>
</template>
<script setup>
import InputLabel from "./InputLabel.vue";
import InputError from "./InputError.vue";
import { watch, defineProps, defineEmits, onMounted, ref } from "vue";
import { useForm } from "@inertiajs/vue3";
import vSelect from "vue-select";
import Swal from "sweetalert2";

const props = defineProps({
  isOpen: {
    type: Boolean,
    required: true,
  },
  title: {
    type: String,
    required: true,
  },
  menuId: {
    type: [Number, String], // Allow both Number and String
    required: true,
  },
  menuroles: {
    type: Array, // Roles should be an array
    required: true,
  },
  roles: {
    type: Array, // Roles should be an array
    required: true,
  },
  programs: {
    type: Array, // Programs should be an array
    required: true,
  },
  users: {
    type: Array, // Users should be an array
    required: true,
  },
});
const emit = defineEmits(["update:isOpen"]);

const roleId = ref([]);
const prodiId = ref([]);
const userId = ref([]);
const form = useForm({
  id:
    props.menuroles.length > 0
      ? props.menuroles.find((menurole) => menurole.menu_id == props.menuId)?.id
      : null,
  menu_id: props.menuId, // Get menuId from props
  role_id: roleId.value, // Access the value of the ref
  user_id: userId.value, // Access the value of the ref
  idprodi: prodiId.value, // Access the value of the ref
});

// Watch for changes in the refs and update the form accordingly
watch(roleId, (newRoleId) => {
  form.role_id = newRoleId;
});

watch(prodiId, (newProdiId) => {
  form.idprodi = newProdiId;
});
watch(userId, (newUserId) => {
  form.user_id = newUserId;
});
// Watch for changes in props.menuId
watch(
  () => props.menuId,
  (newMenuId) => {
    form.menu_id = newMenuId;
  }
);

const submit = () => {
  form.put(route("menuroles.update", form.id), {
    onSuccess: () => {
      form.reset();
      Swal.fire({
        title: "Berhasil!",
        text: "Data berhasil diperbarui.",
        icon: "success",
        confirmButtonText: "OK",
      }).then(() => {
        window.location.reload();
      });
    },
  });
};

const closeModal = () => {
  emit("update:isOpen", false);
};

const handleClickOutside = (event) => {
  if (event.target == event.currentTarget) {
    closeModal();
  }
};

// Fungsi untuk mencari role_id berdasarkan menuId
const updateRoleId = () => {
  const menurole = props.menuroles.find(
    (menurole) => menurole.menu_id == props.menuId
  );
  if (menurole) {
    roleId.value = Array.isArray(menurole.roles) ? menurole.roles : [];
  } else {
    roleId.value = []; // Nilai default jika tidak ada role yang cocok
  }
};

// Fungsi untuk mencari idprodi berdasarkan menuId
const updateProdiId = () => {
  const menurole = props.menuroles.find(
    (menurole) => menurole.menu_id == props.menuId
  );
  if (menurole) {
    prodiId.value = Array.isArray(menurole.studyprograms)
      ? menurole.studyprograms.map((program) => program.id)
      : [];
  } else {
    prodiId.value = []; // Nilai default jika tidak ada role yang cocok
  }
};

// Fungsi untuk mencari user_id berdasarkan menuId
const updateUserId = () => {
  const menurole = props.menuroles.find(
    (menurole) => menurole.menu_id == props.menuId
  );

  if (menurole) {
    userId.value = Array.isArray(menurole.users)
      ? menurole.users.map((user) => user.id)
      : [];
  } else {
    userId.value = []; // Default value if no matching role is found
  }
};

// Saat komponen dimount, atur role_id berdasarkan menuId
onMounted(() => {
  updateRoleId();
  updateProdiId();
  updateUserId();
});

// Watcher untuk menangani perubahan menuId setelah komponen dimuat
watch(
  () => props.menuId,
  () => {
    updateRoleId();
    updateProdiId();
    updateUserId();
  }
);
</script>
<style scoped></style>
